import { LoginOrigin } from '@/models/User';
import { getLocaleFromUrl } from './i18n';
import { clearContentRelatedStorage, clearUserRelatedStorage, getToken } from './storage';

const locale = getLocaleFromUrl();

export const handleUnauthorizedRequest = (afterLoginRedirect?: string) => {
  const platform = sessionStorage.getItem('platform') ?? 'WEB';
  const lmsSessionId = sessionStorage.getItem('lmsSessionId');

  clearContentRelatedStorage();
  clearUserRelatedStorage();

  if (
    [LoginOrigin.LTI.toString(), LoginOrigin.LTI13.toString(), LoginOrigin.RS.toString()].includes(
      platform,
    ) &&
    lmsSessionId
  ) {
    window.location.href = `/${locale}/lms/launch-error/lms-unauthorized-request/${lmsSessionId}`;
  } else if (afterLoginRedirect) {
    const redirectWithLocale = `/${locale}${afterLoginRedirect}`;
    window.location.href = `/${locale}/login.html?redirect=${redirectWithLocale}`;
  } else {
    window.location.href = `/${locale}/login.html`;
  }
};

const handleNotFoundRequest = () => {
  window.location.href = `/${locale}/404.html`;
};

const handleNotAvailableRequest = () => {
  window.location.href = 'not-available';
};

const normalizeUrl = (url: RequestInfo | URL) => {
  if (typeof url === 'string') return url;
  else if (url instanceof Request) return url.url;
  else if (url instanceof URL) return url.toString();

  throw new Error('Could not normalize url');
};

const authInterceptor = (url: RequestInfo | URL, options: RequestInit) => {
  const stringUrl = normalizeUrl(url);
  const isApiCall =
    stringUrl.startsWith(import.meta.env.VITE_MY_ENGINE_API) ||
    stringUrl.startsWith(import.meta.env.VITE_COURSE_BFF_API);

  if (isApiCall) {
    const headers = options.headers ?? {};
    const hasAuthenticationHeader =
      headers instanceof Headers && Boolean(headers.get('Authorization'));

    if (!hasAuthenticationHeader) {
      const token = getToken();
      const key = 'Authorization';
      const value = `Bearer ${token}`;

      if (Array.isArray(headers)) headers.push([key, value]);
      else if (headers instanceof Headers) headers.append(key, value);
      else headers[key] = value;

      options.headers = headers;
    }
  }
};

const errorInterceptor = (response: Response) => {
  if (!response.ok) {
    if (response.status === 401) {
      handleUnauthorizedRequest();
    } else if (response.status === 404) {
      handleNotFoundRequest();
    } else if (response.status === 403) {
      handleNotAvailableRequest();
    } else {
      throw new Error(response.statusText);
    }
  }

  return response;
};

const monkeyPatch = async (url: RequestInfo | URL, options: RequestInit = {}) => {
  authInterceptor(url, options);

  return fetch(url, options)?.then(errorInterceptor);
};

export default monkeyPatch;
